import React from 'react';
import { Grid, TableCell, TableRow, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import { Delete, Edit, Loop, PersonAddAlt, Update, WhereToVote } from '@mui/icons-material';
import '../../../components/EventParticipantTable.scss';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import Check from '@mui/icons-material/Check';
import { useCalendar } from '../../../CalendarContext/CalendarContext';
import TextWithTooltip from '../../../../common/ui/TextWithTooltip';
import { UseAuthPermissions } from '../../../../session/UseAuthPermissions';
import { isRescheduleEnabledForOrderPage } from '../../../../utils/AccountUtils';
import {
  AccountData,
  CustomDetailData,
  CustomDetailTypes,
  CustomPersonalDetailData,
  CustomPersonalDetailTypes,
  OrderData,
  OrderPageData,
  ReferralSource,
} from '../../../../api';
import { GuestSide } from '../../../CalendarContext/DeleteDialog/DeleteDialogState';
import { whatsAppLinkFormatter } from '../../../../common/whatsAppLinkFormatter';
import CopyToClipboardButton from '../../../dialogs/CopyToClipboardButton';
import { usePriceRenderer } from '../../../../common/usePriceRenderer';
import { useLanguage } from '../../../../common/GeneralUtils';
import PickupNotificationButton from '../../../dialogs/PickupNotificationButton';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { useEventOrders } from '../../../swr/useEventOrders';
import { TicketBreakdownDisplay } from '../../../../editOrderUiObjects/TicketBreakdownDisplay';
import { findMultipleTicketsUiObject, orderHasMultipleTicketsBreakdown } from '../../../../common/multipleTicketsUtils';

type EventParticipantsRowProps = {
  order: OrderData;
  index: number;
  setOrder: (order: OrderData) => void;
  removeOrder: (order: OrderData) => void;
  setEditOrderIsOpen: (isOpen: boolean) => void;
  setCheckInDialogIsOpen: (isOpen: boolean) => void;
  orderPageCustomPersonalDetails: CustomPersonalDetailData[];
  orderCustomDetails: CustomDetailData[];
  isInEventMode: boolean;
  orderPage: OrderPageData;
  isGuest?: boolean;
  hasGuest?: boolean;
  counterpartyName?: string;
};
const EventParticipantsRow = ({
  order,
  index,
  setOrder,
  setEditOrderIsOpen,
  setCheckInDialogIsOpen,
  removeOrder,
  orderPageCustomPersonalDetails,
  orderCustomDetails,
  isInEventMode,
  orderPage,
  isGuest,
  hasGuest,
  counterpartyName,
}: EventParticipantsRowProps) => {
  const { updateOrder } = useEventOrders({ eventId: order.parent_id });
  const language = useLanguage();
  const { openDeleteOrderDialog } = useCalendar();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { readOnly, isAtLeastSupervisor } = UseAuthPermissions();
  const pickupFeatureEnabled = useFeatureFlagEnabled('feature-pickup-ready');
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { SlimPrice } = usePriceRenderer({
    orderOrVoucher: order,
    initialPrice: order.price,
    returningCustomerFromOrderId: order.returning_customer_from_order_id,
    extraPaymentLines: order.extra_payment_lines,
  });
  const isPartiallyCheckedIn = (order: OrderData) =>
    order.checkin && order.checkin.persons > 0 && order.checkin.persons < order.persons;
  // Added pagination here as front finds it hard to show more then few 10s of lines and starts hanging

  return (
    <TableRow
      key={`participantsTable-${index}`}
      sx={
        isGuest
          ? { boxShadow: `inset ${theme.direction === 'rtl' ? '-3px' : '3px'} 0 0 0 ${theme.palette.mb.control}` }
          : undefined
      }
      className={`table-row${order.checkin && order.checkin.persons === order.persons ? ' checkin-row' : ''}${
        isPartiallyCheckedIn(order) ? ' partially-checkin-row' : ''
      }`}>
      <TableCell align="left" sx={{ padding: '0!important' }}>
        <IconButton
          sx={{ marginInline: isInEventMode ? '20px' : undefined }}
          onClick={() => {
            setOrder(order);
            setCheckInDialogIsOpen(true);
          }}>
          <Tooltip title={t('checkin')} arrow>
            <WhereToVote
              style={{
                width: isInEventMode ? '32px' : undefined,
                height: isInEventMode ? '32px' : undefined,
              }}
            />
          </Tooltip>
        </IconButton>
      </TableCell>
      {!isInEventMode && (
        <>
          <TableCell align="left" sx={{ padding: '0!important' }}>
            <Tooltip title={readOnly ? t('noPermissions') : t('delete')} arrow>
              <span>
                <IconButton
                  style={{ padding: '0px' }}
                  disabled={readOnly}
                  onClick={() => {
                    openDeleteOrderDialog({
                      order,
                      onSubmitCallback: () => removeOrder(order),
                      guestSide: isGuest ? GuestSide.GUEST : hasGuest ? GuestSide.MEMBER : undefined,
                      guestCounterpartyName: counterpartyName,
                    });
                  }}>
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
          </TableCell>
          <TableCell align="left" sx={{ padding: '0!important' }}>
            <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : t('edit')} arrow>
              <span>
                <IconButton
                  style={{ padding: '0px' }}
                  disabled={!isAtLeastSupervisor}
                  onClick={() => {
                    setOrder(order);
                    setEditOrderIsOpen(true);
                  }}>
                  <Edit />
                </IconButton>
              </span>
            </Tooltip>
          </TableCell>
          <TableCell align="left" sx={{ padding: '0!important' }}>
            <Tooltip
              title={
                !isAtLeastSupervisor
                  ? t('noPermissions')
                  : isRescheduleEnabledForOrderPage(account, order.order_page_id)
                  ? t('amend')
                  : t('amend_disabled')
              }
              arrow>
              <span>
                <CopyToClipboardButton
                  IconComponent={Update}
                  width={'18px'}
                  text={`${import.meta.env.VITE_REACT_APP_APP_DOMAIN}/manage-booking?orderid=${order.id}`}
                  orderPageId={order.order_page_id}
                  disabled={!isAtLeastSupervisor || !isRescheduleEnabledForOrderPage(account, order.order_page_id)}
                />
              </span>
            </Tooltip>
          </TableCell>
          {pickupFeatureEnabled && (
            <TableCell align="left" sx={{ padding: '0!important' }}>
              <PickupNotificationButton order={order} account={account} onOrderUpdate={updateOrder} compact />
            </TableCell>
          )}
        </>
      )}
      <TableCell align="left" sx={{ paddingInlineStart: '16px!important' }}>
        <Grid container alignItems="center" gap={1}>
          {(order.returning_customer || order.referral_source === ReferralSource.RETURNING) && (
            <Tooltip
              title={
                order.referral_source === ReferralSource.RETURNING
                  ? t('returnningCustomerfromSource')
                  : t('returnningCustomerfromEmail')
              }
              arrow>
              <Grid container alignItems="center" width="auto" sx={{ color: 'primary.main' }}>
                <Loop />
                {order.returning_customer > 1 && (
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>{order.returning_customer}</span>
                )}
              </Grid>
            </Tooltip>
          )}
          {(isGuest || hasGuest) && (
            <Tooltip
              title={
                isGuest
                  ? counterpartyName
                    ? t('orders.guest_of_named', { name: counterpartyName })
                    : t('orders.guest_of_unknown')
                  : counterpartyName
                  ? t('orders.brought_guest_named', { name: counterpartyName })
                  : t('orders.brought_guest_unknown')
              }
              arrow>
              <Grid container alignItems="center" width="auto" sx={{ color: 'text.secondary' }}>
                <PersonAddAlt fontSize="small" />
              </Grid>
            </Tooltip>
          )}
          <Grid
            item
            style={{
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
            }}
            maxWidth={isSmallScreen ? '120px' : '200px'}>
            {order.pass_id ? (
              <Link
                to={`/dashboard/passes?search=${encodeURIComponent(`${order.firstname} ${order.lastname}`)}`}
                style={{ color: 'inherit', textDecoration: 'underline' }}>
                {`${order.firstname} ${order.lastname}`}
              </Link>
            ) : (
              `${order.firstname} ${order.lastname}`
            )}
          </Grid>
        </Grid>
      </TableCell>
      {orderCustomDetails.map((customDetail: CustomDetailData) => {
        if (customDetail.type === CustomDetailTypes.BOOL) {
          const detailIsTrue = order.custom_details?.[customDetail.name] === true;
          return <TableCell align="center">{detailIsTrue && <Check />}</TableCell>;
        }
        const detail = order.custom_details?.[customDetail.name];
        if (!detail) {
          return null;
        }
        return (
          <TableCell
            align="left"
            style={{
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
              maxWidth: isSmallScreen ? '120px' : '200px',
            }}>
            <Typography>{detail}</Typography>
          </TableCell>
        );
      })}
      {orderHasMultipleTicketsBreakdown(order, findMultipleTicketsUiObject(orderPage)) ? (
        <TableCell align="left">
          <TicketBreakdownDisplay order={order} orderPage={orderPage} variant="compact" />
        </TableCell>
      ) : (
        <TableCell align="center">
          <span>{isPartiallyCheckedIn(order) ? `${order.checkin?.persons}/${order.persons}` : order.persons}</span>
        </TableCell>
      )}

      {orderPageCustomPersonalDetails
        .filter((x: CustomPersonalDetailData) => !x.hide_from_console)
        .map((customDetail: CustomPersonalDetailData) => {
          if (customDetail.type === CustomPersonalDetailTypes.BOOLEAN) {
            const detailIsTrue = order.custom_personal_details?.[customDetail.id] === true;
            return <TableCell align="center">{detailIsTrue && <Check />}</TableCell>;
          }
          return (
            <TableCell
              align="left"
              style={{
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
                maxWidth: isSmallScreen ? '120px' : '200px',
              }}>
              <Typography />
              {order.custom_personal_details?.[customDetail.id]}
            </TableCell>
          );
        })}

      {orderPage.objects
        .filter((x: any) => x.type === 'toggleButtonGroup')
        .map((x: any) => {
          const detailName = x.name;
          const detail = order.custom_details?.[detailName];
          if (!detail) {
            return null;
          }
          const option = x.options.find((option: any) => option.value === detail);
          return <TableCell align="left">{option ? option.label[language] : x.options[0].label[language]}</TableCell>;
        })}

      <TableCell align="left">
        {order.comment && (
          <TextWithTooltip width={isSmallScreen ? '120px' : '200px'} text={order.comment} tooltip={order.comment} />
        )}
      </TableCell>
      <TableCell align="left">
        <Grid container gap={1} alignItems="center">
          <CopyToClipboardButton width={'18px'} color={theme.palette.text.secondary} text={order.phone} />

          <a className="link" target="_blank" href={whatsAppLinkFormatter(order.phone, '')} rel="noreferrer">
            {order.phone}
          </a>
        </Grid>
      </TableCell>
      <TableCell align="left">#{order.order_number}</TableCell>
      <TableCell align="left">
        <SlimPrice />
      </TableCell>

      <TableCell align="left">{t(`order_status.${order.status.toLowerCase()}`)}</TableCell>
    </TableRow>
  );
};

export default EventParticipantsRow;
